import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Alert, Snackbar,
  IconButton, Tooltip, Chip, MenuItem, Select, FormControl, InputLabel,
  InputAdornment, CircularProgress, Divider, Autocomplete, Stack,
} from "@mui/material";
import {
  Add, Search, Refresh, Description, Cancel, Visibility,
  ShoppingCart, DeleteOutline, PictureAsPdf, Email, Receipt,
  CheckCircle,
} from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const ESTADO = {
  PENDIENTE:  { color: "warning",  label: "Pendiente" },
  CONFIRMADO: { color: "info",     label: "Confirmado" },
  COMPLETADO: { color: "success",  label: "Completado" },
  CANCELADO:  { color: "error",    label: "Cancelado" },
};

const emptyCliente = { clienteId: "", razonSocial: "", identificacion: "", email: "", telefono: "", direccion: "" };
const emptyCondiciones = { plazoEntrega: "Inmediata", formaPago: "Contado", plazoCredito: 0, observaciones: "" };

export default function Cotizaciones() {
  const navigate = useNavigate();
  // ── Lista ──
  const [cotizaciones, setCotizaciones]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [busqueda, setBusqueda]           = useState("");
  const [snackbar, setSnackbar]           = useState({ open: false, message: "", severity: "success" });

  // ── Formulario nueva ──
  const [dialogNueva, setDialogNueva]     = useState(false);
  const [clientes, setClientes]           = useState([]);
  const [productos, setProductos]         = useState([]);
  const [clienteForm, setClienteForm]     = useState({ ...emptyCliente });
  const [items, setItems]                 = useState([]);
  const [condiciones, setCondiciones]     = useState({ ...emptyCondiciones });
  const [fechaVenc, setFechaVenc]         = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [loadingGuardar, setLoadingGuardar] = useState(false);

  // ── Detalle ──
  const [dialogDetalle, setDialogDetalle] = useState(false);
  const [sel, setSel]                     = useState(null);
  const [loadingPdf, setLoadingPdf]       = useState(false);
  const [dialogEmail, setDialogEmail]     = useState(false);
  const [emailDest, setEmailDest]         = useState("");
  const [emailCC, setEmailCC]             = useState("");
  const [loadingEmail, setLoadingEmail]   = useState(false);

  // ── Convertir a factura ──
  const [dialogFacturar, setDialogFacturar] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
  const show = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  // Fecha de mañana como default
  const fechaMañana = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  // ── Carga de cotizaciones ──
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tipo: "PROFORMA" });
      if (busqueda) params.append("busqueda", busqueda);
      const res = await axios.get(`${API_URL}/api/cotizaciones?${params}`, { headers: headers() });
      setCotizaciones(res.data?.data || []);
    } catch {
      show("Error al cargar cotizaciones", "error");
    } finally {
      setLoading(false);
    }
  }, [busqueda]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Abrir formulario nueva ──
  const abrirNueva = async () => {
    setClienteForm({ ...emptyCliente });
    setItems([]);
    setCondiciones({ ...emptyCondiciones });
    setFechaVenc(fechaMañana());
    setObservaciones("");
    try {
      const [rc, rp] = await Promise.all([
        axios.get(`${API_URL}/api/clientes`, { headers: headers() }),
        axios.get(`${API_URL}/api/productos`, { headers: headers() }),
      ]);
      setClientes(Array.isArray(rc.data) ? rc.data : []);
      setProductos(Array.isArray(rp.data) ? rp.data : []);
    } catch {}
    setDialogNueva(true);
  };

  // ── Cliente ──
  const selCliente = (c) => {
    if (!c) { setClienteForm({ ...emptyCliente }); return; }
    setClienteForm({
      clienteId:    c.id || "",
      razonSocial:  c.razon_social || c.nombre || "",
      identificacion: c.identificacion || "",
      email:        Array.isArray(c.emails) ? c.emails[0] || "" : c.email || "",
      telefono:     c.telefono_celular || c.telefono_convencional || "",
      direccion:    c.direccion_1 || c.direccion || "",
    });
  };

  // ── Productos ──
  const agregarProducto = (p) => {
    if (!p) return;
    setItems(prev => {
      const idx = prev.findIndex(i => i.productoId === p.id);
      if (idx >= 0) {
        return prev.map((item, i) =>
          i === idx
            ? { ...item, cantidad: item.cantidad + 1, precioTotal: (item.cantidad + 1) * item.precioUnitario }
            : item
        );
      }
      const precio = parseFloat(p.precio_1 || p.precio_venta || 0);
      return [...prev, {
        productoId: p.id || "",
        codigo: p.codigo || "",
        descripcion: p.nombre || "",
        cantidad: 1,
        unidad: p.unidad || "Unidad",
        precioUnitario: precio,
        descuento: 0,
        precioTotal: precio,
        iva: (p.iva_porcentaje === "0%" || p.iva === "0" || p.iva === 0) ? "0%" : "15%",
      }];
    });
  };

  const editarItem = (idx, campo, val) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const u = { ...item, [campo]: val };
      const cant  = campo === "cantidad"       ? Number(val) : u.cantidad;
      const prec  = campo === "precioUnitario" ? Number(val) : u.precioUnitario;
      const desc  = campo === "descuento"      ? Number(val) : u.descuento;
      const base  = cant * prec;
      u.precioTotal = parseFloat((base - (desc > 1 ? desc : base * (desc / 100))).toFixed(4));
      return u;
    }));
  };

  // ── Totales ──
  const subtotal15   = items.filter(i => i.iva !== "0%").reduce((s, i) => s + i.precioTotal, 0);
  const subtotal0    = items.filter(i => i.iva === "0%").reduce((s, i) => s + i.precioTotal, 0);
  const subtotalBase = subtotal15 + subtotal0;
  const iva          = subtotal15 * 0.15;
  const total        = subtotalBase + iva;

  // ── Guardar cotización ──
  const guardar = async () => {
    if (!clienteForm.razonSocial && !clienteForm.identificacion)
      return show("Ingrese los datos del cliente", "error");
    if (items.length === 0)
      return show("Agregue al menos un producto", "error");
    if (!fechaVenc)
      return show("La fecha de vencimiento es obligatoria", "error");

    setLoadingGuardar(true);
    try {
      await axios.post(`${API_URL}/api/cotizaciones`, {
        tipo: "PROFORMA",
        fecha: new Date().toISOString(),
        fechaVencimiento: fechaVenc,
        cliente: clienteForm,
        detalles: items,
        condiciones,
        observaciones,
      }, { headers: headers() });
      show("Cotización creada");
      setDialogNueva(false);
      cargar();
    } catch (e) {
      show(e.response?.data?.message || "Error al guardar", "error");
    } finally {
      setLoadingGuardar(false);
    }
  };

  // ── Cancelar cotización ──
  const cancelar = async (id) => {
    try {
      await axios.put(`${API_URL}/api/cotizaciones/${id}/estado`,
        { estado: "CANCELADO", comentario: "Cancelado" },
        { headers: headers() });
      show("Cotización cancelada");
      cargar();
      setDialogDetalle(false);
    } catch (e) { show(e.response?.data?.message || "Error", "error"); }
  };

  // ── Eliminar cotización ──
  const eliminar = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/cotizaciones/${id}`, { headers: headers() });
      show("Cotización eliminada");
      cargar();
      setDialogDetalle(false);
    } catch (e) { show(e.response?.data?.message || "Error", "error"); }
  };

  // ── Descargar PDF ──
  const descargarPdf = async (cotizacion) => {
    setLoadingPdf(true);
    try {
      const res = await axios.get(`${API_URL}/api/cotizaciones/${cotizacion.id}/pdf`, {
        headers: headers(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cotizacion_${cotizacion.numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { show("Error al generar PDF", "error"); }
    finally { setLoadingPdf(false); }
  };

  // ── Enviar email ──
  const abrirEmail = (c) => {
    setSel(c);
    setEmailDest(c.cliente?.email || "");
    setEmailCC("");
    setDialogEmail(true);
  };

  const enviarEmail = async () => {
    if (!emailDest) return show("Ingrese el email del destinatario", "error");
    setLoadingEmail(true);
    try {
      await axios.post(`${API_URL}/api/cotizaciones/${sel.id}/email`,
        { destinatario: emailDest, cc: emailCC || undefined },
        { headers: headers() });
      show(`Cotización enviada a ${emailDest}${emailCC ? ` (CC: ${emailCC})` : ""}`);
      setDialogEmail(false);
    } catch (e) { show(e.response?.data?.message || "Error al enviar email", "error"); }
    finally { setLoadingEmail(false); }
  };

  // ── Convertir a factura ──
  const abrirFacturar = (c) => {
    setSel(c);
    setDialogFacturar(true);
  };

  const irACrearFactura = () => {
    if (!sel) return;
    // Guardar datos en sessionStorage en el formato exacto que usa CrearFactura.js
    const datos = {
      cliente: {
        nombre:    sel.cliente?.razonSocial || "",
        ruc:       sel.cliente?.identificacion || "",
        email:     sel.cliente?.email || "",
        direccion: sel.cliente?.direccion || "",
        telefono:  sel.cliente?.telefono || "",
      },
      detalles: (sel.detalles || []).map(d => ({
        codigo:    d.codigo || "",
        nombre:    d.descripcion || "",
        cantidad:  d.cantidad || 1,
        precio:    d.precioUnitario || 0,
        descuento: d.descuento || 0,
        iva:       d.iva || "15%",
      })),
      cotizacionId:     sel.id,
      cotizacionNumero: sel.numero,
    };
    sessionStorage.setItem("cotizacion_prefill", JSON.stringify(datos));
    // Marcar como completada en el backend
    axios.put(
      `${API_URL}/api/cotizaciones/${sel.id}/estado`,
      { estado: "COMPLETADO", comentario: "Aprobada por el cliente" },
      { headers: headers() }
    ).catch(() => {});
    setDialogFacturar(false);
    navigate("/crear-factura");
  };

  // ── Filtro local ──
  const cotizacionesFiltradas = cotizaciones.filter(c => {
    if (!busqueda) return true;
    const t = busqueda.toLowerCase();
    return c.numero?.toLowerCase().includes(t) ||
      c.cliente?.razonSocial?.toLowerCase().includes(t) ||
      c.cliente?.identificacion?.includes(t);
  });

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      {/* ── Encabezado ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>📋 Cotizaciones</Typography>
          <Typography variant="body2" color="text.secondary">Proformas y propuestas comerciales</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={abrirNueva} sx={{ borderRadius: 2 }}>
          Nueva Cotización
        </Button>
      </Box>

      {/* ── Buscador ── */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small" placeholder="Buscar por número o cliente..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)}
            sx={{ width: 320 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <Tooltip title="Actualizar"><IconButton onClick={cargar}><Refresh /></IconButton></Tooltip>
        </Stack>
      </Paper>

      {/* ── Tabla ── */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ background: "#1a237e" }}>
              <TableRow>
                {["Número", "Cliente", "Fecha", "Vencimiento", "Total", "Estado", "Acciones"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 700 }} align={h === "Total" ? "right" : h === "Acciones" ? "center" : "left"}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
              ) : cotizacionesFiltradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Description sx={{ fontSize: 48, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
                    <Typography color="text.secondary">No hay cotizaciones</Typography>
                  </TableCell>
                </TableRow>
              ) : cotizacionesFiltradas.map(c => {
                const est = ESTADO[c.estado] || ESTADO.PENDIENTE;
                const vencida = c.fechaVencimiento && new Date(c.fechaVencimiento) < new Date();
                return (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="primary">{c.numero}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{c.cliente?.razonSocial || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.cliente?.identificacion}</Typography>
                    </TableCell>
                    <TableCell>{c.fecha ? new Date(c.fecha).toLocaleDateString("es-EC") : "—"}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={vencida ? "error" : "text.primary"} fontWeight={vencida ? 700 : 400}>
                        {c.fechaVencimiento ? new Date(c.fechaVencimiento).toLocaleDateString("es-EC") : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color="primary">${c.totales?.total?.toFixed(2) || "0.00"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={est.label} color={est.color} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Ver detalle">
                          <IconButton size="small" color="primary" onClick={() => { setSel(c); setDialogDetalle(true); }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Descargar PDF">
                          <IconButton size="small" color="secondary" onClick={() => descargarPdf(c)}>
                            {loadingPdf ? <CircularProgress size={14} /> : <PictureAsPdf fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enviar por email">
                          <IconButton size="small" color="info" onClick={() => abrirEmail(c)}>
                            <Email fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {c.estado === "PENDIENTE" && (
                          <>
                            <Tooltip title="Convertir a factura">
                              <IconButton size="small" color="success" onClick={() => abrirFacturar(c)}>
                                <Receipt fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Anular">
                              <IconButton size="small" color="warning" onClick={() => { if (window.confirm(`¿Anular cotización ${c.numero}?`)) cancelar(c.id); }}>
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => { if (window.confirm(`¿Eliminar cotización ${c.numero}? Esta acción no se puede deshacer.`)) eliminar(c.id); }}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ════════════════════════════════════
          DIALOG: NUEVA COTIZACIÓN
      ════════════════════════════════════ */}
      <Dialog open={dialogNueva} onClose={() => setDialogNueva(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: "#1a237e", color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          <ShoppingCart /> Nueva Cotización
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={3}>

            {/* ── Sección CLIENTE ── */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
                  👤 Datos del Cliente
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={clientes}
                      getOptionLabel={c => `${c.razon_social || c.nombre || ""} — ${c.identificacion || ""}`}
                      onChange={(_, v) => selCliente(v)}
                      renderOption={(props, c) => (
                        <li {...props} key={c.id || c.identificacion}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{c.razon_social || c.nombre}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.identificacion}</Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={params => <TextField {...params} label="Buscar cliente existente" size="small" fullWidth />}
                      noOptionsText="Sin resultados — complete los campos manualmente"
                      ListboxProps={{ style: { maxHeight: 260 } }}
                      componentsProps={{
                        paper: { sx: { minWidth: 420, maxWidth: 600 } },
                        popper: { style: { minWidth: 420 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Razón Social *"
                      value={clienteForm.razonSocial}
                      onChange={e => setClienteForm(p => ({ ...p, razonSocial: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="RUC / Cédula"
                      value={clienteForm.identificacion}
                      onChange={e => setClienteForm(p => ({ ...p, identificacion: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Email"
                      value={clienteForm.email}
                      onChange={e => setClienteForm(p => ({ ...p, email: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Teléfono"
                      value={clienteForm.telefono}
                      onChange={e => setClienteForm(p => ({ ...p, telefono: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Dirección"
                      value={clienteForm.direccion}
                      onChange={e => setClienteForm(p => ({ ...p, direccion: e.target.value }))} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ── Sección PRODUCTOS ── */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
                  📦 Productos / Servicios
                </Typography>
                <Autocomplete
                  options={productos}
                  getOptionLabel={p => `${p.nombre || ""} — ${p.codigo || ""} — $${parseFloat(p.precio_1 || p.precio_venta || 0).toFixed(2)}`}
                  onChange={(_, v) => { agregarProducto(v); }}
                  value={null}
                  renderInput={params => <TextField {...params} label="Buscar y agregar producto" size="small" fullWidth />}
                  noOptionsText="Sin resultados"
                  ListboxProps={{ style: { maxHeight: 260 } }}
                  componentsProps={{
                    paper: { sx: { minWidth: 480, maxWidth: 640 } },
                    popper: { style: { minWidth: 480 } },
                  }}
                  sx={{ mb: 2 }}
                />

                {items.length > 0 ? (
                  <>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead sx={{ background: "#f5f5f5" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                            <TableCell align="center" width={90} sx={{ fontWeight: 700 }}>Cantidad</TableCell>
                            <TableCell align="right" width={120} sx={{ fontWeight: 700 }}>Precio Unit.</TableCell>
                            <TableCell align="center" width={100} sx={{ fontWeight: 700 }}>Desc %</TableCell>
                            <TableCell align="right" width={120} sx={{ fontWeight: 700 }}>Total</TableCell>
                            <TableCell width={44} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item, i) => (
                            <TableRow key={i} sx={{ "&:hover": { background: "#fafafa" } }}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{item.descripcion}</Typography>
                                <Typography variant="caption" color="text.secondary">{item.codigo}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <TextField size="small" type="number"
                                  inputProps={{ min: 1, style: { textAlign: "center" } }}
                                  value={item.cantidad}
                                  onChange={e => editarItem(i, "cantidad", e.target.value)}
                                  sx={{ width: 78 }} />
                              </TableCell>
                              <TableCell align="right">
                                <TextField size="small" type="number"
                                  inputProps={{ min: 0, step: 0.01, style: { textAlign: "right" } }}
                                  value={item.precioUnitario}
                                  onChange={e => editarItem(i, "precioUnitario", e.target.value)}
                                  sx={{ width: 100 }} />
                              </TableCell>
                              <TableCell align="center">
                                <TextField size="small" type="number"
                                  inputProps={{ min: 0, max: 100, style: { textAlign: "center" } }}
                                  value={item.descuento}
                                  onChange={e => editarItem(i, "descuento", e.target.value)}
                                  sx={{ width: 80 }} />
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={700} color="primary">
                                  ${item.precioTotal.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <IconButton size="small" color="error" onClick={() => setItems(p => p.filter((_, j) => j !== i))}>
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Totales */}
                    <Box sx={{ mt: 1.5, p: 2, background: "#f8f9ff", borderRadius: 2, border: "1px solid #e3e8ff" }}>
                      <Grid container justifyContent="flex-end">
                        <Grid item xs={12} sm={5}>
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Subtotal 15%:</Typography>
                              <Typography variant="body2">${subtotal15.toFixed(2)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Subtotal 0%:</Typography>
                              <Typography variant="body2">${subtotal0.toFixed(2)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">IVA 15%:</Typography>
                              <Typography variant="body2">${iva.toFixed(2)}</Typography>
                            </Stack>
                            <Divider />
                            <Stack direction="row" justifyContent="space-between">
                              <Typography fontWeight={700} color="primary">TOTAL:</Typography>
                              <Typography fontWeight={700} color="primary" fontSize={18}>${total.toFixed(2)}</Typography>
                            </Stack>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ p: 3, textAlign: "center", border: "2px dashed #e0e0e0", borderRadius: 2 }}>
                    <Typography color="text.secondary" variant="body2">
                      Busque y agregue productos o servicios
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* ── Sección CONDICIONES ── */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
                  📋 Condiciones
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Forma de Pago</InputLabel>
                      <Select value={condiciones.formaPago} label="Forma de Pago"
                        onChange={e => setCondiciones(p => ({ ...p, formaPago: e.target.value }))}>
                        {["Contado", "Crédito", "Transferencia", "Cheque"].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Plazo de Entrega</InputLabel>
                      <Select value={condiciones.plazoEntrega} label="Plazo de Entrega"
                        onChange={e => setCondiciones(p => ({ ...p, plazoEntrega: e.target.value }))}>
                        {["Inmediata", "1-3 días", "1 semana", "2 semanas", "1 mes"].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth size="small" label="Fecha de Vencimiento *"
                      type="date" InputLabelProps={{ shrink: true }}
                      value={fechaVenc}
                      onChange={e => setFechaVenc(e.target.value)}
                      error={!fechaVenc}
                      helperText={!fechaVenc ? "Obligatorio" : "La cotización expira esta fecha"}
                    />                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth size="small" label="Observaciones"
                      value={observaciones}
                      onChange={e => setObservaciones(e.target.value)} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDialogNueva(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={loadingGuardar}
            startIcon={loadingGuardar ? <CircularProgress size={16} /> : <Description />}>
            {loadingGuardar ? "Guardando..." : "Guardar Cotización"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════════════════════════════
          DIALOG: DETALLE
      ════════════════════════════════════ */}
      <Dialog open={dialogDetalle} onClose={() => setDialogDetalle(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        {sel && (() => {
          const est = ESTADO[sel.estado] || ESTADO.PENDIENTE;
          return (
            <>
              <DialogTitle sx={{ background: "#1a237e", color: "#fff" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={700} fontSize={18}>{sel.numero}</Typography>
                  <Chip label={est.label} color={est.color} />
                </Box>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Fechas */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Fecha</Typography>
                    <Typography>{sel.fecha ? new Date(sel.fecha).toLocaleDateString("es-EC", { year: "numeric", month: "long", day: "numeric" }) : "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Válida hasta</Typography>
                    <Typography color={sel.fechaVencimiento && new Date(sel.fechaVencimiento) < new Date() ? "error" : "text.primary"} fontWeight={600}>
                      {sel.fechaVencimiento ? new Date(sel.fechaVencimiento).toLocaleDateString("es-EC", { year: "numeric", month: "long", day: "numeric" }) : "—"}
                    </Typography>
                  </Grid>

                  {/* Cliente */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>👤 Cliente</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2"><strong>{sel.cliente?.razonSocial || "Consumidor Final"}</strong></Typography>
                          <Typography variant="caption" color="text.secondary">{sel.cliente?.identificacion}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="caption" color="text.secondary">Email</Typography>
                          <Typography variant="body2">{sel.cliente?.email || "—"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="caption" color="text.secondary">Teléfono</Typography>
                          <Typography variant="body2">{sel.cliente?.telefono || "—"}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Productos */}
                  <Grid item xs={12}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead sx={{ background: "#1a237e" }}>
                          <TableRow>
                            {["Descripción", "Código", "Cant.", "Precio", "Desc%", "Total"].map(h => (
                              <TableCell key={h} sx={{ color: "#fff", fontWeight: 700 }}
                                align={["Precio", "Total"].includes(h) ? "right" : "left"}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sel.detalles?.map((d, i) => (
                            <TableRow key={i} sx={{ background: i % 2 ? "#fafafa" : "#fff" }}>
                              <TableCell>{d.descripcion}</TableCell>
                              <TableCell><Typography variant="caption">{d.codigo}</Typography></TableCell>
                              <TableCell>{d.cantidad}</TableCell>
                              <TableCell align="right">${parseFloat(d.precioUnitario || 0).toFixed(2)}</TableCell>
                              <TableCell>{d.descuento > 0 ? `${d.descuento}%` : "—"}</TableCell>
                              <TableCell align="right"><strong>${parseFloat(d.precioTotal || 0).toFixed(2)}</strong></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  {/* Totales */}
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, background: "#f8f9ff", borderRadius: 2, border: "1px solid #e3e8ff" }}>
                      <Grid container justifyContent="flex-end">
                        <Grid item xs={12} sm={4}>
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                              <Typography variant="body2">${parseFloat(sel.totales?.subtotal || sel.totales?.total || 0).toFixed(2)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">IVA 15%:</Typography>
                              <Typography variant="body2">${parseFloat(sel.totales?.iva || 0).toFixed(2)}</Typography>
                            </Stack>
                            <Divider />
                            <Stack direction="row" justifyContent="space-between">
                              <Typography fontWeight={700} color="primary">TOTAL:</Typography>
                              <Typography fontWeight={700} color="primary" fontSize={18}>${parseFloat(sel.totales?.total || 0).toFixed(2)}</Typography>
                            </Stack>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Condiciones */}
                  {(sel.condiciones?.formaPago || sel.observaciones) && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>📋 Condiciones</Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Forma de pago</Typography>
                            <Typography variant="body2">{sel.condiciones?.formaPago}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Plazo de entrega</Typography>
                            <Typography variant="body2">{sel.condiciones?.plazoEntrega}</Typography>
                          </Grid>
                          {sel.observaciones && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Observaciones</Typography>
                              <Typography variant="body2">{sel.observaciones}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
                <Button startIcon={<PictureAsPdf />} variant="outlined" color="secondary"
                  onClick={() => descargarPdf(sel)} disabled={loadingPdf}>
                  {loadingPdf ? "Generando..." : "Descargar PDF"}
                </Button>
                <Button startIcon={<Email />} variant="outlined" color="info"
                  onClick={() => { setDialogDetalle(false); abrirEmail(sel); }}>
                  Enviar por Email
                </Button>
                {sel.estado === "PENDIENTE" && (
                  <>
                    <Button startIcon={<Receipt />} variant="contained" color="success"
                      onClick={() => { setDialogDetalle(false); abrirFacturar(sel); }}>
                      Convertir a Factura
                    </Button>
                    <Button startIcon={<Cancel />} variant="outlined" color="error"
                      onClick={() => cancelar(sel.id)}>
                      Cancelar
                    </Button>
                  </>
                )}
                <Button startIcon={<DeleteOutline />} color="error"
                  onClick={() => eliminar(sel.id)}>
                  Eliminar
                </Button>
                <Button onClick={() => setDialogDetalle(false)} color="inherit">Cerrar</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ════════════════════════════════════
          DIALOG: ENVIAR EMAIL
      ════════════════════════════════════ */}
      <Dialog open={dialogEmail} onClose={() => setDialogEmail(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: "#1a237e", color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          <Email /> Enviar Cotización por Email
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Se enviará la cotización <strong>{sel?.numero}</strong> en formato PDF.
          </Typography>
          <TextField
            fullWidth label="Email del cliente *" type="email"
            value={emailDest} onChange={e => setEmailDest(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Correo principal del destinatario"
          />
          <TextField
            fullWidth label="Correos adicionales (CC)" type="text"
            value={emailCC} onChange={e => setEmailCC(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="correo1@mail.com, correo2@mail.com"
            helperText="Separar múltiples correos con coma"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogEmail(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={enviarEmail} disabled={loadingEmail}
            startIcon={loadingEmail ? <CircularProgress size={16} /> : <Email />}>
            {loadingEmail ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════════════════════════════
          DIALOG: CONVERTIR A FACTURA
      ════════════════════════════════════ */}
      <Dialog open={dialogFacturar} onClose={() => setDialogFacturar(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: "#2e7d32", color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircle /> Convertir a Factura
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            El cliente aprobó la cotización <strong>{sel?.numero}</strong>
          </Alert>
          <Typography variant="body2">
            Se abrirá el formulario de facturación con los datos precargados desde esta cotización.
          </Typography>
          <Box sx={{ mt: 2, p: 2, background: "#f1f8e9", borderRadius: 2 }}>
            <Typography variant="body2"><strong>Cliente:</strong> {sel?.cliente?.razonSocial}</Typography>
            <Typography variant="body2"><strong>Total:</strong> ${sel?.totales?.total?.toFixed(2)}</Typography>
            <Typography variant="body2"><strong>Items:</strong> {sel?.detalles?.length} producto(s)</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogFacturar(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" color="success" startIcon={<Receipt />} onClick={irACrearFactura}>
            Ir a Facturar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} variant="filled"
          onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
